import { useMemo, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ChatFlow } from './chat'
import { X, Trash2, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ParameterInput } from './parameter-input'
import { DeleteConversationDialog } from './delete-conversation-dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useDatabaseData } from '../page/hooks'
import { useTeamStore } from '@/lib/hooks/useTeam'
import { Combobox } from '../ui/combobox'
import { app } from '@/lib/app'

interface WorkerConfigPanelProps {
  selectedWorker: AIWorker | null
  onClose: () => void
  showChat: boolean
  chatHistory: ChatHistory
  onChatHistoryChange: (history: ChatHistory) => void
  isLoadingHistory?: boolean
  availableConversations?: Array<{ uid: string; lastMessage: string; timestamp: string }>
  selectedConversationUid?: string | null
  isNewConversation?: boolean
  onConversationSelect?: (uid: string) => void
  agentId?: string
}

const EXCLUDED_PARAMS = new Set(['history', 'maxHistory', 'sumarizationModel', 'sumarizePrompt'])

export function WorkerConfigPanel({
  selectedWorker,
  onClose,
  showChat,
  chatHistory,
  onChatHistoryChange,
  isLoadingHistory = false,
  availableConversations = [],
  selectedConversationUid = null,
  isNewConversation = false,
  onConversationSelect,
  agentId
}: WorkerConfigPanelProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [conversationPopoverOpen, setConversationPopoverOpen] = useState(false)

  const { selectedTeam } = useTeamStore()
  let { data: contactsData, loading: contactsLoading, error: contactsError } = useDatabaseData("contacts", { filter: (supa) => supa.eq("team", selectedTeam?.id).eq("type", "operator"), })
  contactsData = contactsData || []
  const contacts = contactsData.map((c: any) => ({ value: c.id, label: c.name }))


  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp)
      const today = new Date()
      const isToday = date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()

      if (isToday) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
      }
    } catch (e) {
      return timestamp
    }
  }

  const handleHistoryCleared = () => {
    const emptyHistory: ChatHistory = []
    onChatHistoryChange(emptyHistory)
    setShowDeleteDialog(false)
  }

  const inputsToRender = useMemo(() => {
    if (!selectedWorker) return []

    const parameters = Object.entries(selectedWorker.parameters)
      .filter(([key]) => selectedWorker.registry.type === 'chatHistory' || !EXCLUDED_PARAMS.has(key))
      .map(([key, value]) => ({
        key,
        value,
        label: key.replace(/_/g, ' '),
        source: 'parameters' as const,
      }))

    const fields = Object.entries(selectedWorker.fields)
      .filter(([, value]) => value.default !== undefined || value.mock !== undefined)
      .filter(([key]) => !selectedWorker.parameters.hasOwnProperty(key))
      .map(([key, value]) => ({
        key,
        value: value.mock ?? value.default,
        label: value.title || key.replace(/_/g, ' '),
        source: 'fields' as const,
      }))

    return [...parameters, ...fields]
  }, [selectedWorker, selectedWorker?.parameters, selectedWorker?.fields])

  if (!selectedWorker && !showChat) return null

  const selectedContact = localStorage.getItem("selectedContact") || null

  const [contact, setContact] = useState(selectedContact)

  function onSelectedContactChanged(c) {
    localStorage.setItem("selectedContact", c)
    setContact(c)
  }

  return (
    <div className="w-full bg-white border-l border-gray-200 flex flex-col" style={{ height: 'calc(100vh - 80px)' }}>
      <div className="flex items-center w-full justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {selectedWorker ? `${selectedWorker.registry.title} Configuration` : 'Chat'}
        </h3>
        <div className="flex items-center gap-2">
          {showChat && !selectedWorker && (
            <Popover open={conversationPopoverOpen} onOpenChange={setConversationPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  title="Select conversation"
                >
                  <Clock className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-2">
                  <button
                    onClick={() => {
                      if (onConversationSelect) {
                        onConversationSelect('new')
                      }
                      setConversationPopoverOpen(false)
                    }}
                    className="w-full text-left p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">New Conversation</span>
                      <span className="text-xs text-gray-500">Start fresh</span>
                    </div>
                  </button>
                  {availableConversations.length > 0 && (
                    <div className="mt-2 border-t border-gray-200 pt-2">
                      <div className="max-h-64 overflow-y-auto space-y-1">
                        {availableConversations.map((conv) => (
                          <button
                            key={conv.uid}
                            onClick={() => {
                              if (onConversationSelect) {
                                onConversationSelect(conv.uid)
                              }
                              setConversationPopoverOpen(false)
                            }}
                            className={`w-full text-left p-2 hover:bg-gray-100 rounded-lg transition-colors ${selectedConversationUid === conv.uid && !isNewConversation
                              ? 'bg-blue-50 hover:bg-blue-100'
                              : ''
                              }`}
                          >
                            <div className="flex flex-col">
                              <span className="text-sm truncate">{conv.lastMessage}</span>
                              <span className="text-xs text-gray-500">{formatTimestamp(conv.timestamp)}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
          {chatHistory.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <Tabs defaultValue={selectedWorker ? "config" : "chat"} className="h-full flex flex-col">
          {selectedWorker && (
            <TabsList className="grid grid-cols-2 mx-4 mt-4">
              <TabsTrigger value="config">Configuration</TabsTrigger>
              <TabsTrigger value="chat">Chat</TabsTrigger>
            </TabsList>
          )}

          {selectedWorker && (
            <TabsContent value="config" className="flex-1 overflow-y-auto overflow-x-hidden p-4">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Worker Type</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    {selectedWorker.type}
                  </p>
                </div>
                <div className="space-y-4">
                  {inputsToRender.map(({ key, value, label, source }) => (
                    <div key={`${source}-${key}`}>
                      <ParameterInput
                        workerId={selectedWorker.id}
                        paramKey={key}
                        paramValue={value}
                        source={source}
                        workerType={selectedWorker.type}
                        label={label}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}

          <TabsContent value="chat" className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="h-full">
              {isLoadingHistory ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading conversation history...</p>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col">
                  <div className="px-3 pt-3">
                    <Combobox items={contacts} placeholder="Select a Contact..." value={selectedContact} onChange={onSelectedContactChanged} />
                  </div>
                  {chatHistory.length === 0 && (
                    <div className="text-center text-gray-500 p-4 border-b border-gray-200">
                      <p className="text-sm">No conversation history found</p>
                      <p className="text-xs mt-1">Start a new conversation with this agent</p>
                    </div>
                  )}
                  <div className="flex-1">
                    <ChatFlow
                      contact={selectedContact || app.agent?.debuguuid || undefined}
                      history={chatHistory}
                      onHistoryChange={onChatHistoryChange}
                    />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <DeleteConversationDialog
        contact={selectedContact}
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onHistoryCleared={handleHistoryCleared}
      />
    </div>
  )
} 
